import { useState, useEffect, useRef } from "react";
import { apiFetch } from "../../api/api";
import {
  CATEGORIES, CATEGORY_GROUPS, EMPTY_PRODUCT
} from "../data/Productstore";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg: "#FAF7F2", bgSoft: "#F5EFE6", card: "#FFFFFF",
  gold: "#C8A46A", goldD: "#B28C4B", goldL: "#F5EDE0",
  champagne: "#E8D7BC", border: "#EFE7DA",
  success: "#6FAE5C", warning: "#E8B04A", danger: "#E27C7C",
  textPri: "#2B2B2B", textSec: "#777777",
};

// ─── UI atoms ─────────────────────────────────────────────────────────────────
const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider"
      style={{ color: T.textSec }}>
      {label} {required && <span style={{ color: T.danger }}>*</span>}
    </label>
    {children}
  </div>
);

const Input = ({ value, onChange, placeholder, type = "text", className = "" }) => (
  <input type={type} value={value || ""} onChange={onChange} placeholder={placeholder}
    className={`w-full px-3 py-2.5 rounded-xl text-[13px] outline-none transition-all ${className}`}
    style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri,
      fontFamily: "'DM Sans',sans-serif" }}
    onFocus={e => e.target.style.borderColor = T.gold}
    onBlur={e => e.target.style.borderColor = T.border} />
);

const Textarea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea value={value || ""} onChange={onChange} placeholder={placeholder} rows={rows}
    className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none transition-all resize-none"
    style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri,
      fontFamily: "'DM Sans',sans-serif" }}
    onFocus={e => e.target.style.borderColor = T.gold}
    onBlur={e => e.target.style.borderColor = T.border} />
);

const Select = ({ value, onChange, children }) => (
  <select value={value || ""} onChange={onChange}
    className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
    style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }}>
    {children}
  </select>
);

const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl bg-white p-4 sm:p-5 ${className}`}
    style={{ border: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
    {children}
  </div>
);

const SectionTitle = ({ children }) => (
  <h3 className="text-[13px] font-bold mb-4 pb-2"
    style={{ color: T.textPri, borderBottom: `1px solid ${T.border}`,
      fontFamily: "'Cormorant Garamond',Georgia,serif", fontSize: 16 }}>
    {children}
  </h3>
);

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <div className="relative flex-shrink-0" onClick={() => onChange(!checked)}>
      <div className="w-10 h-5 rounded-full transition-colors"
        style={{ background: checked ? "#FFD600" : T.border }} />
      <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
        style={{ left: checked ? "22px" : "2px" }} />
    </div>
    <span className="text-[12px]" style={{ color: T.textSec }}>{label}</span>
  </label>
);

const StatusBadge = ({ status }) => {
  const map = {
    active:   { bg: "#EEFAF0", color: "#4CAF6E", label: "Active" },
    draft:    { bg: "#F5F5F5", color: "#9E9E9E", label: "Draft" },
    archived: { bg: "#FFF0F0", color: T.danger,  label: "Archived" },
  };
  const s = map[status] || map.draft;
  return <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full" style={{ background: s.bg, color: s.color }}>{s.label}</span>;
};

// ─── Color Picker Row ─────────────────────────────────────────────────────────
function ColorManager({ colors = [], onChange }) {
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#C8A46A");

  const add = () => {
    if (!name.trim()) return;
    onChange([...colors, { name: name.trim(), hex }]);
    setName(""); setHex("#C8A46A");
  };

  const remove = (i) => onChange(colors.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {colors.map((c, i) => (
          <div key={i} className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px]"
            style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
            <div className="w-4 h-4 rounded-full border border-white shadow-sm" style={{ background: c.hex }} />
            <span style={{ color: T.textPri }}>{c.name}</span>
            <button onClick={() => remove(i)} className="ml-1 text-gray-400 hover:text-red-500 text-xs leading-none">×</button>
          </div>
        ))}
        {colors.length === 0 && <p className="text-[11px]" style={{ color: T.textSec }}>No colors added yet</p>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input type="color" value={hex} onChange={e => setHex(e.target.value)}
          className="w-9 h-9 rounded-lg cursor-pointer border-0 p-0.5 flex-shrink-0"
          style={{ background: T.bgSoft, border: `1px solid ${T.border}` }} />
        <input type="text" value={name} onChange={e => setName(e.target.value)}
          placeholder="Color name (e.g. Navy Blue)"
          className="flex-1 min-w-[140px] px-3 py-2 rounded-xl text-[12px] outline-none"
          style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }}
          onKeyDown={e => e.key === "Enter" && add()} />
        <button onClick={add}
          className="px-3 py-2 rounded-xl text-[12px] font-semibold text-white flex-shrink-0"
          style={{ background: T.gold }}>+ Add</button>
      </div>
    </div>
  );
}

// ─── Image Upload Manager (backend connected) ──────────────────────────────────
// ─── Image Upload Manager (backend connected) — Cover aur Additional images alag-alag ──
function ImageManager({ images = [], onChange }) {
  const coverRef = useRef();
  const otherRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const cover = images[0] || null;
  const others = images.slice(1);

  const uploadOne = async (file) => {
    const formData = new FormData();
    formData.append("image", file);
    const token = localStorage.getItem("aishcraft_admin_token");
    const res = await fetch("https://aishcraft.com/backend/api/products/upload-image.php", {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    const data = await res.json();
    if (!res.ok || data.success === false) throw new Error(data.message || "Upload failed");
    return { url: data.data.url, alt: file.name.replace(/\.[^/.]+$/, "") };
  };

  // ── Cover slot: hamesha index 0, kabhi bhi auto-shift nahi hoga ──
  const handleCoverUpload = async (fileList) => {
    const file = Array.from(fileList).find(f => f.type.startsWith("image/"));
    if (!file) return;
    setUploading(true); setUploadError("");
    try {
      const img = await uploadOne(file);
      onChange(prev => [img, ...prev.slice(1)]);
    } catch (err) {
      setUploadError(err.message || "Failed to upload cover image.");
    } finally { setUploading(false); }
  };
  const removeCover = () => onChange(prev => prev.slice(1));
  const updateCoverAlt = (alt) => onChange(prev => prev.map((img, i) => i === 0 ? { ...img, alt } : img));

  // ── Additional images: hamesha end mein add hote hain, kabhi cover slot mein nahi jaate ──
  const handleOtherUpload = async (fileList) => {
    const validFiles = Array.from(fileList).filter(f => f.type.startsWith("image/"));
    if (!validFiles.length) return;
    setUploading(true); setUploadError("");
    for (const file of validFiles) {
      try {
        const img = await uploadOne(file);
        onChange(prev => (prev.length === 0 ? [null, img] : [...prev, img]));
        // agar cover khaali hai to bhi ye slot 0 nahi legi — thoda neeche fix karte hain save ke time
      } catch (err) {
        setUploadError(err.message || "Failed to upload one or more images.");
      }
    }
    setUploading(false);
  };
  const removeOther = (idx) => onChange(prev => prev.filter((_, i) => i !== idx + 1));
  const updateOtherAlt = (idx, alt) => onChange(prev => prev.map((img, i) => i === idx + 1 ? { ...img, alt } : img));

  return (
    <div className="space-y-5">
      {/* ── Cover Image — dedicated single slot ── */}
      <div>
        <p className="text-[11px] font-semibold mb-2 uppercase tracking-wider" style={{ color: T.textSec }}>Cover Image</p>
        {cover ? (
          <div className="relative group w-32 sm:w-36">
            <div className="aspect-square rounded-xl overflow-hidden" style={{ border: `2px solid ${T.gold}` }}>
              <img src={cover.url} alt={cover.alt} className="w-full h-full object-cover" />
              <div className="absolute top-1 left-1 text-[9px] font-bold text-white px-1.5 py-0.5 rounded-full" style={{ background: T.gold }}>COVER</div>
            </div>
            <div className="absolute inset-0 rounded-xl flex items-center justify-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(0,0,0,0.45)" }}>
              <button onClick={() => coverRef.current.click()} className="text-white text-[10px] font-semibold px-2 py-1 rounded bg-black/40 hover:bg-black/60">Change</button>
              <button onClick={removeCover} className="text-white text-[10px] font-semibold px-2 py-1 rounded bg-red-500/70 hover:bg-red-500">Remove</button>
            </div>
            <input type="text" value={cover.alt} onChange={e => updateCoverAlt(e.target.value)}
              placeholder="Alt text" className="mt-1 w-full px-2 py-1 rounded-lg text-[10px] outline-none"
              style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }} />
          </div>
        ) : (
          <div onClick={() => coverRef.current.click()}
            className="w-32 sm:w-36 aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer hover:border-amber-400 transition-all"
            style={{ borderColor: T.border, opacity: uploading ? 0.6 : 1, pointerEvents: uploading ? "none" : "auto" }}>
            <div className="text-2xl mb-1">📸</div>
            <p className="text-[10px] text-center px-2" style={{ color: T.textSec }}>Click to upload cover</p>
          </div>
        )}
        <input ref={coverRef} type="file" accept="image/*" className="hidden"
          onChange={e => { handleCoverUpload(e.target.files); e.target.value = ""; }} />
        <p className="text-[10px] mt-1" style={{ color: T.textSec }}>Ye image website pe product ka main cover dikhegi.</p>
      </div>

      {/* ── Additional Images — grid, alag drop zone ── */}
      <div>
        <p className="text-[11px] font-semibold mb-2 uppercase tracking-wider" style={{ color: T.textSec }}>Additional Images</p>
        <div
          className="border-2 border-dashed rounded-2xl p-5 sm:p-6 text-center cursor-pointer transition-all hover:border-amber-400"
          style={{ borderColor: T.border, opacity: uploading ? 0.6 : 1, pointerEvents: uploading ? "none" : "auto" }}
          onClick={() => otherRef.current.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); handleOtherUpload(e.dataTransfer.files); }}>
          <div className="text-3xl mb-2">{uploading ? "⏳" : "🖼️"}</div>
          <p className="text-[13px] font-medium" style={{ color: T.textSec }}>
            {uploading ? "Uploading..." : <>Drop images here or <span style={{ color: T.gold }}>click to upload</span></>}
          </p>
          <p className="text-[11px] mt-1" style={{ color: T.textSec }}>PNG, JPG, WebP — Max 5MB each</p>
          <input ref={otherRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => { handleOtherUpload(e.target.files); e.target.value = ""; }} />
        </div>

        {uploadError && <p className="text-[11px] text-center mt-2" style={{ color: T.danger }}>{uploadError}</p>}

        {others.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5 sm:gap-3 mt-3">
            {others.map((img, i) => (
              <div key={i} className="relative group">
                <div className="aspect-square rounded-xl overflow-hidden" style={{ border: `2px solid ${T.border}` }}>
                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                </div>
                <button onClick={() => removeOther(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full text-white flex items-center justify-center text-[10px] opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                  style={{ background: T.danger }}>×</button>
                <input type="text" value={img.alt} onChange={e => updateOtherAlt(i, e.target.value)}
                  placeholder="Alt text" className="mt-1 w-full px-2 py-1 rounded-lg text-[10px] outline-none"
                  style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// ─── Tag Input (local only — kept for UI parity, not sent to backend) ─────────
function TagInput({ tags = [], onChange }) {
  const [input, setInput] = useState("");
  const PRESETS = ["featured", "bestseller", "new-arrival", "trending", "sale", "corporate"];

  const add = (tag) => {
    const t = tag.trim().toLowerCase().replace(/\s+/g, "-");
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInput("");
  };
  const remove = (t) => onChange(tags.filter(x => x !== t));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {tags.map(t => (
          <span key={t} className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full font-medium"
            style={{ background: T.goldL, color: T.goldD, border: `1px solid ${T.champagne}` }}>
            {t}
            <button onClick={() => remove(t)} className="hover:text-red-500 ml-0.5">×</button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          placeholder="Add tag..."
          className="flex-1 px-3 py-2 rounded-xl text-[12px] outline-none"
          style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }}
          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(input); } }} />
        <button onClick={() => add(input)}
          className="px-3 py-2 rounded-xl text-[12px] font-semibold text-white flex-shrink-0"
          style={{ background: T.gold }}>+ Add</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {PRESETS.filter(p => !tags.includes(p)).map(p => (
          <button key={p} onClick={() => add(p)}
            className="text-[10px] px-2 py-0.5 rounded-full border hover:bg-amber-50 transition-colors"
            style={{ borderColor: T.border, color: T.textSec }}>+ {p}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Product Form (Add/Edit) ───────────────────────────────────────────────────
function ProductForm({ product: initialProduct, onSave, onCancel, saving }) {
  const [form, setForm] = useState(() => ({ ...EMPTY_PRODUCT, ...initialProduct }));
  const [images, setImages] = useState(initialProduct?.images || []);
  const [activeTab, setActiveTab] = useState("basic");

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setSpec = (key, val) => setForm(f => ({ ...f, specs: { ...f.specs, [key]: val } }));
  const setSEO = (key, val) => setForm(f => ({ ...f, seo: { ...f.seo, [key]: val } }));
  const setDelivery = (key, val) => setForm(f => ({ ...f, delivery: { ...f.delivery, [key]: val } }));

  const handleTitle = (val) => {
    set("title", val);
    if (!form.slug || form.slug === slugify(form.title)) {
      set("slug", slugify(val));
    }
    if (!form.seo.title) setSEO("title", `${val} | Aishcraft`);
  };
  const slugify = (str) => str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleSave = () => {
    if (!form.title.trim()) return alert("Product title required!");
    if (!form.price) return alert("Price required!");
    if (!form.categoryId) return alert("Category required!");
    onSave({ ...form, images, slug: form.slug || slugify(form.title) });
  };

  const TABS = ["basic", "media", "pricing", "variants", "delivery", "seo", "visibility"];

  return (
    <div className="fixed inset-0 z-50 flex" style={{ background: "rgba(0,0,0,0.4)" }}>
      <div className="m-0 sm:m-auto w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] rounded-none sm:rounded-3xl overflow-hidden flex flex-col"
        style={{ background: T.card, boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0"
          style={{ borderBottom: `1px solid ${T.border}` }}>
          <div>
            <h2 className="text-[16px] sm:text-[18px] font-bold" style={{ color: T.textPri, fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
              {initialProduct?.id ? "Edit Product" : "Add New Product"}
            </h2>
            <p className="text-[11px]" style={{ color: T.textSec }}>
              {initialProduct?.id ? `Editing: ${initialProduct.title}` : "Fill in the product details below"}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={onCancel} disabled={saving}
              className="px-3.5 sm:px-4 py-2 rounded-xl text-[12px] sm:text-[13px] border font-medium disabled:opacity-50"
              style={{ borderColor: T.border, color: T.textSec }}>Cancel</button>
            <button onClick={() => { set("status", "draft"); handleSave(); }} disabled={saving}
              className="px-3.5 sm:px-4 py-2 rounded-xl text-[12px] sm:text-[13px] border font-semibold disabled:opacity-60"
              style={{ borderColor: T.gold, color: T.gold }}>{saving ? "Saving..." : "Save Draft"}</button>
            <button onClick={() => { set("status", "active"); handleSave(); }} disabled={saving}
              className="px-3.5 sm:px-4 py-2 rounded-xl text-[12px] sm:text-[13px] font-semibold text-white disabled:opacity-60"
              style={{ background: T.gold }}>{saving ? "Saving..." : "Publish Product"}</button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 px-4 sm:px-6 flex-shrink-0 overflow-x-auto"
          style={{ borderBottom: `1px solid ${T.border}` }}>
          {TABS.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className="px-3 sm:px-4 py-3 text-[12px] font-semibold capitalize transition-all flex-shrink-0 whitespace-nowrap"
              style={activeTab === tab
                ? { color: T.gold, borderBottom: `2px solid ${T.gold}` }
                : { color: T.textSec }}>
              {tab}
            </button>
          ))}
        </div>

        {/* Scrollable body */}
     <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5" data-lenis-prevent>

          {/* ── BASIC ── */}
          {activeTab === "basic" && (
            <>
              <Card>
                <SectionTitle>Product Information</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Field label="Product Title" required>
                      <Input value={form.title} onChange={e => handleTitle(e.target.value)} placeholder="e.g. Getting Things Done" />
                    </Field>
                  </div>
                  <Field label="Subtitle / Type">
                    <Input value={form.subtitle} onChange={e => set("subtitle", e.target.value)} placeholder="e.g. Daily Planner" />
                  </Field>
                  <Field label="URL Slug" required>
                    <Input value={form.slug} onChange={e => set("slug", slugify(e.target.value))} placeholder="getting-things-done" />
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Short Description">
                      <Textarea value={form.description} onChange={e => set("description", e.target.value)}
                        placeholder="A no-fuss daily layout built for people who run on checklists..." rows={2} />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Full Description">
                      <Textarea value={form.longDescription} onChange={e => set("longDescription", e.target.value)}
                        placeholder="Detailed product description..." rows={4} />
                    </Field>
                  </div>
                </div>
              </Card>

              <Card>
                <SectionTitle>Category</SectionTitle>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="Category" required>
                    <Select value={form.categoryId} onChange={e => set("categoryId", e.target.value)}>
                      <option value="">— Select Category —</option>
                      {CATEGORY_GROUPS.map(group => (
                        <optgroup key={group} label={group}>
                          {CATEGORIES.filter(c => c.group === group).map(c => (
                            <option key={c.id} value={c.id}>{c.label}</option>
                          ))}
                        </optgroup>
                      ))}
                    </Select>
                  </Field>
                  <Field label="Tags">
                    <div className="mt-1">
                      <TagInput tags={form.tags} onChange={val => set("tags", val)} />
                    </div>
                  </Field>
                </div>
              </Card>
            </>
          )}

          {/* ── MEDIA ── */}
          {activeTab === "media" && (
            <Card>
              <SectionTitle>Product Images</SectionTitle>
              <p className="text-[11px] mb-4" style={{ color: T.textSec }}>
                First image becomes the cover. Recommended: 800×800px, white background.
              </p>
              <ImageManager images={images} onChange={setImages} />
            </Card>
          )}

          {/* ── PRICING ── */}
          {activeTab === "pricing" && (
            <Card>
              <SectionTitle>Pricing & Inventory</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Field label="MRP / Regular Price (₹)" required>
                    <Input value={form.price} onChange={e => set("price", e.target.value)} placeholder="1799" type="number" />
                  </Field>
                </div>
                <Field label="Sale Price (₹)">
                  <Input value={form.salePrice} onChange={e => set("salePrice", e.target.value)} placeholder="1599" type="number" />
                </Field>
                <Field label="Discount">
                  <div className="px-3 py-2.5 rounded-xl text-[13px] font-semibold"
                    style={{ background: T.goldL, color: T.goldD, border: `1px solid ${T.champagne}` }}>
                    {form.price && form.salePrice
                      ? `${Math.round(((form.price - form.salePrice) / form.price) * 100)}% OFF`
                      : "—"}
                  </div>
                </Field>
                <Field label="SKU">
                  <Input value={form.sku} onChange={e => set("sku", e.target.value)} placeholder="AC-DP-001" />
                </Field>
                <Field label="Stock Quantity">
                  <Input value={form.stock} onChange={e => set("stock", e.target.value)} placeholder="48" type="number" />
                </Field>
              </div>
            </Card>
          )}

          {/* ── VARIANTS ── */}
          {activeTab === "variants" && (
            <>
              <Card>
                <SectionTitle>Color Variants</SectionTitle>
                <ColorManager colors={form.colors} onChange={val => set("colors", val)} />
              </Card>
              <Card>
                <SectionTitle>Product Specifications</SectionTitle>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    ["pages", "No. of Pages", "365"],
                    ["size", "Size", "A5"],
                    ["cover", "Cover Type", "Hardcover"],
                    ["binding", "Binding", "Thread Sewn"],
                    ["paper", "Paper Quality", "90 GSM"],
                    ["weight", "Weight", "400g"],
                  ].map(([key, label, ph]) => (
                    <Field key={key} label={label}>
                      <Input value={form.specs[key]} onChange={e => setSpec(key, e.target.value)} placeholder={ph} />
                    </Field>
                  ))}
                </div>
              </Card>
              <Card>
                <SectionTitle>Personalization</SectionTitle>
                <div className="space-y-3">
                  <Toggle checked={form.isPersonalizable} onChange={val => set("isPersonalizable", val)}
                    label="This product can be personalized" />
                  {form.isPersonalizable && (
                    <>
                      <Field label="Personalize Button Label">
                        <Input value={form.personalizeLabel} onChange={e => set("personalizeLabel", e.target.value)}
                          placeholder="Personalise It" />
                      </Field>

                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <Field label="Name Font (CSS font-family value)">
                          <Input value={form.personalizeFont} onChange={e => set("personalizeFont", e.target.value)}
                            placeholder="'Dancing Script', cursive" />
                        </Field>
                        <Field label="Font Size (px)">
                          <Input value={form.personalizeFontSize} onChange={e => set("personalizeFontSize", e.target.value)}
                            placeholder="22px" />
                        </Field>
                      </div>

                      <Field label="Name Color">
                        <div className="flex gap-2 items-center">
                          <input type="color" value={form.personalizeColor || "#1A1A1A"}
                            onChange={e => set("personalizeColor", e.target.value)}
                            className="w-9 h-9 rounded-lg cursor-pointer border-0 p-0.5 flex-shrink-0"
                            style={{ background: T.bgSoft, border: `1px solid ${T.border}` }} />
                          <Input value={form.personalizeColor} onChange={e => set("personalizeColor", e.target.value)}
                            placeholder="#D4A574" />
                        </div>
                      </Field>

      <Field label="Name Position — click on the image where the name should appear">
  {images.length > 0 ? (
    <div
      className="relative w-full max-w-[240px] mx-auto rounded-xl overflow-hidden cursor-crosshair select-none"
      style={{ border: `2px solid ${T.border}`, aspectRatio: "4 / 5" }}
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;
        const leftPercent = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
        const bottomPercent = Math.max(0, Math.min(100, ((rect.height - clickY) / rect.height) * 100));
        set("personalizePosition", `${bottomPercent.toFixed(1)}%,${leftPercent.toFixed(1)}%`);
      }}
    >
     <img
  src={images[0].url}
  alt="cover preview"
  className="w-full h-full object-cover pointer-events-none"
/>
      {form.personalizePosition && (() => {
        const [bottom, left] = form.personalizePosition.split(",");
        return (
          <div
            className="absolute pointer-events-none text-center px-2"
            style={{
              bottom: bottom || "52%",
              left: left || "50%",
              transform: `translateX(-50%) rotate(${form.personalizeRotation || 0}deg)`,
            }}
          >
            <span
              className="inline-block px-1"
              style={{
                fontFamily: form.personalizeFont || "'Dancing Script', cursive",
                color: form.personalizeColor || "#1A1A1A",
                fontSize: form.personalizeFontSize || "18px",
                textShadow: "0 0 2px rgba(255,255,255,0.6)",
              }}
            >
              Name
            </span>
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 mx-auto mt-0.5" />
          </div>
        );
      })()}
    </div>
  ) : (
    <p className="text-[11px]" style={{ color: T.textSec }}>
      Upload a cover image first (Media tab) to set the name position visually.
    </p>
  )}
  <div className="flex items-center gap-2 mt-2">
    <span className="text-[11px]" style={{ color: T.textSec }}>Position (bottom%,left%):</span>
    <Input value={form.personalizePosition} onChange={e => set("personalizePosition", e.target.value)}
      placeholder="52%,50%" className="!w-28" />
  </div>
</Field>

<Field label="Name Tilt / Rotation (degrees — match your cover's angle)">
  <Input value={form.personalizeRotation} onChange={e => set("personalizeRotation", e.target.value)}
    placeholder="e.g. -8 or 8" type="number" />
</Field>


                    </>
                  )}
                </div>
              </Card>
            </>
          )}

          {/* ── DELIVERY ── */}
          {activeTab === "delivery" && (
            <Card>
              <SectionTitle>Delivery Information</SectionTitle>
              <div className="space-y-4">
                <Field label="Delivery Info Text">
                  <Input value={form.delivery.info} onChange={e => setDelivery("info", e.target.value)}
                    placeholder="Ships between 9 Jul and 13 Jul" />
                </Field>
                <Field label="Free Delivery Above (₹)">
                  <Input value={form.delivery.freeAbove} onChange={e => setDelivery("freeAbove", e.target.value)}
                    placeholder="500" type="number" />
                </Field>
              </div>
            </Card>
          )}

          {/* ── SEO ── */}
          {activeTab === "seo" && (
            <Card>
              <SectionTitle>SEO & Meta</SectionTitle>
              <div className="space-y-4">
                <Field label="SEO Title">
                  <Input value={form.seo.title} onChange={e => setSEO("title", e.target.value)}
                    placeholder="Getting Things Done Daily Planner | Aishcraft" />
                  <p className="text-[10px] mt-1" style={{ color: form.seo.title?.length > 60 ? T.danger : T.textSec }}>
                    {form.seo.title?.length || 0}/60 characters
                  </p>
                </Field>
                <Field label="Meta Description">
                  <Textarea value={form.seo.description} onChange={e => setSEO("description", e.target.value)}
                    placeholder="A no-fuss daily layout built for people who run on checklists, time-blocks and finished to-dos." rows={2} />
                  <p className="text-[10px] mt-1" style={{ color: form.seo.description?.length > 160 ? T.danger : T.textSec }}>
                    {form.seo.description?.length || 0}/160 characters
                  </p>
                </Field>
                <Field label="Keywords">
                  <Input value={form.seo.keywords} onChange={e => setSEO("keywords", e.target.value)}
                    placeholder="daily planner, productivity planner, aishcraft" />
                </Field>
                {form.seo.title && (
                  <div className="p-4 rounded-xl overflow-hidden" style={{ background: T.bgSoft, border: `1px solid ${T.border}` }}>
                    <p className="text-[11px] mb-2 font-semibold" style={{ color: T.textSec }}>Search Preview:</p>
                    <p className="text-[14px] text-blue-600 font-medium truncate">{form.seo.title}</p>
                    <p className="text-[12px] text-green-700 truncate">aishcraft.com/{CATEGORIES.find(c => c.id === form.categoryId)?.path?.slice(1) || "products"}/{form.slug}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{form.seo.description}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* ── VISIBILITY ── */}
          {activeTab === "visibility" && (
            <Card>
              <SectionTitle>Visibility & Labels</SectionTitle>
              <div className="space-y-4">
                <Field label="Product Status">
                  <Select value={form.status} onChange={e => set("status", e.target.value)}>
                    <option value="active">Active — Visible on website</option>
                    <option value="draft">Draft — Hidden from website</option>
                    <option value="archived">Archived — Removed from listings</option>
                  </Select>
                </Field>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 pt-2">
                  <Toggle checked={form.isFeatured}   onChange={v => set("isFeatured", v)}   label="Featured Product" />
                  <Toggle checked={form.isBestSeller} onChange={v => set("isBestSeller", v)} label="Best Seller" />
                  <Toggle checked={form.isNewArrival} onChange={v => set("isNewArrival", v)} label="New Arrival" />
                  <Toggle checked={form.isTrending}   onChange={v => set("isTrending", v)}   label="Trending" />
                  <Toggle checked={form.isSoldOut}    onChange={v => set("isSoldOut", v)}    label="Sold Out" />
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCTS PAGE (List view) — backend connected
// ═══════════════════════════════════════════════════════════════════════════════
export default function ProductsAdmin() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving]     = useState(false);

  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [search, setSearch]       = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selected, setSelected]   = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [tab, setTab]             = useState("all");

  // ── Fetch products from backend ─────────────────────────────────────────────
  const fetchProducts = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/products/list.php");
      setProducts(res.data || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  // ── Convert specs object -> highlight bullet strings for backend ───────────
  const specsToHighlights = (specs) => {
    if (!specs) return [];
    const labels = { pages: "Pages", size: "Size", cover: "Cover", binding: "Binding", paper: "Paper", weight: "Weight" };
    return Object.entries(specs)
      .filter(([, v]) => v && String(v).trim() !== "")
      .map(([k, v]) => `${labels[k] || k}: ${v}`);
  };

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        sku: formData.sku,
        slug: formData.slug,
        subtitle: formData.subtitle,
        description: formData.description,
        categoryId: formData.categoryId,
        price: formData.price,
        salePrice: formData.salePrice,
        stock: formData.stock,
        status: formData.status,
        isFeatured: formData.isFeatured,
        isBestSeller: formData.isBestSeller,
        isNewArrival: formData.isNewArrival,
        isTrending: formData.isTrending,
        isSoldOut: formData.isSoldOut,
        isPersonalizable: formData.isPersonalizable,
        personalizeFont: formData.personalizeFont,
        personalizeColor: formData.personalizeColor,
        personalizePosition: formData.personalizePosition,
        personalizeFontSize: formData.personalizeFontSize,
        personalizeLabel: formData.personalizeLabel,
        personalizeRotation: formData.personalizeRotation,
        seo: formData.seo,
        colors: formData.colors,
        highlights: specsToHighlights(formData.specs),
        images: formData.images,
      };

      if (formData.id) {
        await apiFetch("/products/update.php", {
          method: "PUT",
          body: { id: formData.id, ...payload },
        });
      } else {
        await apiFetch("/products/create.php", {
          method: "POST",
          body: payload,
        });
      }
      await fetchProducts();
      setShowForm(false); setEditing(null);
    } catch (err) {
      alert(err.message || "Something went wrong while saving the product.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch("/products/delete.php", {
        method: "DELETE",
        body: { id },
      });
      await fetchProducts();
      setSelected(s => s.filter(x => x !== id));
    } catch (err) {
      alert(err.message || "Failed to delete product.");
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!confirm(`Delete ${selected.length} selected product(s)?`)) return;
    try {
      for (const id of selected) {
        await apiFetch("/products/delete.php", { method: "DELETE", body: { id } });
      }
      await fetchProducts();
      setSelected([]);
    } catch (err) {
      alert(err.message || "Failed to delete some products.");
    }
  };

  // ── Filtered list ──
  const filtered = products.filter(p => {
    const matchSearch = !search || p.title.toLowerCase().includes(search.toLowerCase()) || p.sku?.toLowerCase().includes(search.toLowerCase());

    // categoryId (DB numeric id) frontend ke CATEGORIES list ke id (slug-jaisa) se match nahi karta,
    // isliye category NAME se compare karte hain (jaise "Products by Category" section mein hota hai)
    const selectedCategoryLabel = CATEGORIES.find(c => c.id === filterCat)?.label || "";
    const matchCat = !filterCat || (p.categoryName || "").trim().toLowerCase() === selectedCategoryLabel.trim().toLowerCase();

    const matchStatus = !filterStatus || p.status === filterStatus;
    const matchTab = tab === "all" || (tab === "active" && p.status === "active") || (tab === "draft" && p.status === "draft") || (tab === "featured" && p.isFeatured) || (tab === "low-stock" && Number(p.stock) < 10);
    return matchSearch && matchCat && matchStatus && matchTab;
  });

  const TABS = [
    { id: "all",       label: "All Products", count: products.length },
    { id: "active",    label: "Active",       count: products.filter(p => p.status === "active").length },
    { id: "draft",     label: "Draft",        count: products.filter(p => p.status === "draft").length },
    { id: "featured",  label: "Featured",     count: products.filter(p => p.isFeatured).length },
    { id: "low-stock", label: "Low Stock",    count: products.filter(p => Number(p.stock) < 10).length },
  ];

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-[20px] sm:text-[24px] font-bold" style={{ color: T.textPri, fontFamily: "'Cormorant Garamond',Georgia,serif" }}>
            Products
          </h1>
          <p className="text-[12px]" style={{ color: T.textSec }}>
            {products.length} products across {CATEGORIES.length} categories
          </p>
        </div>
        <div className="flex gap-2">
          <button className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-[12px] border font-medium"
            style={{ borderColor: T.border, color: T.textSec }}>
            ↓ Export CSV
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2.5 rounded-xl text-[13px] font-semibold text-white"
            style={{ background: T.gold }}>
            + Add Product
          </button>
        </div>
      </div>

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading products...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchProducts}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          {/* Summary cards */}
          <div
            className="grid gap-2.5 sm:gap-3"
            style={{ gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))" }}
          >
            {[
              ["Total",      products.length,                              T.goldL,    T.goldD],
              ["Active",     products.filter(p => p.status === "active").length,   "#EEFAF0", "#4CAF6E"],
              ["Draft",      products.filter(p => p.status === "draft").length,    "#F5F5F5", "#9E9E9E"],
              ["Featured",   products.filter(p => p.isFeatured).length,            T.goldL,    T.goldD],
              ["Low Stock",  products.filter(p => Number(p.stock) < 10).length,   "#FFF8EC", T.warning],
            ].map(([label, count, bg, color]) => (
              <div key={label} className="rounded-2xl p-3.5 sm:p-4 bg-white flex items-center gap-2.5 sm:gap-3 min-w-0"
                style={{ border: `1px solid ${T.border}` }}>
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center text-base sm:text-lg flex-shrink-0" style={{ background: bg }}>📦</div>
                <div className="min-w-0">
                  <p className="text-[16px] sm:text-[18px] font-bold" style={{ color }}>{count}</p>
                  <p className="text-[10px] truncate" style={{ color: T.textSec }}>{label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-2xl w-full sm:w-fit overflow-x-auto" style={{ background: T.bgSoft }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-[12px] font-medium transition-all flex-shrink-0 whitespace-nowrap"
                style={tab === t.id ? { background: "white", color: T.gold, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" } : { color: T.textSec }}>
                {t.label}
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                  style={{ background: tab === t.id ? T.goldL : T.border, color: tab === t.id ? T.goldD : T.textSec }}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          {/* Filters + search */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-3">
            <div className="relative flex-1 sm:max-w-sm">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
              </span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by title or SKU..."
                className="w-full pl-9 pr-4 py-2.5 text-[12px] rounded-xl outline-none"
                style={{ background: "#fff", border: `1px solid ${T.border}`, color: T.textPri }} />
            </div>
            <div className="flex gap-2.5 sm:gap-3">
              <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
                className="flex-1 sm:flex-none text-[12px] px-3 py-2.5 rounded-xl outline-none"
                style={{ border: `1px solid ${T.border}`, background: "#fff", color: T.textSec }}>
                <option value="">All Categories</option>
                {CATEGORY_GROUPS.map(g => (
                  <optgroup key={g} label={g}>
                    {CATEGORIES.filter(c => c.group === g).map(c => (
                      <option key={c.id} value={c.id}>{c.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                className="flex-1 sm:flex-none text-[12px] px-3 py-2.5 rounded-xl outline-none"
                style={{ border: `1px solid ${T.border}`, background: "#fff", color: T.textSec }}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            {selected.length > 0 && (
              <button onClick={handleBulkDelete}
                className="px-3 py-2 rounded-xl text-[12px] font-medium whitespace-nowrap"
                style={{ background: "#FFF0F0", color: T.danger, border: `1px solid ${T.danger}30` }}>
                🗑 Delete ({selected.length})
              </button>
            )}
          </div>

          {/* Products table */}
          <div className="rounded-2xl overflow-hidden bg-white" style={{ border: `1px solid ${T.border}` }}>
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <div className="text-4xl mb-3">📦</div>
                <p className="text-[14px] font-semibold" style={{ color: T.textPri }}>No products found</p>
                <p className="text-[12px] mt-1" style={{ color: T.textSec }}>Try changing filters or add a new product</p>
                <button onClick={() => setShowForm(true)}
                  className="mt-4 px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white"
                  style={{ background: T.gold }}>+ Add First Product</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                      <th className="px-4 py-3 w-8">
                        <input type="checkbox"
                          checked={selected.length === filtered.length && filtered.length > 0}
                          onChange={e => setSelected(e.target.checked ? filtered.map(p => p.id) : [])}
                          style={{ accentColor: T.gold }} />
                      </th>
                      {["Product","Category","Price","Stock","Status","Labels","Actions"].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider"
                          style={{ color: T.textSec }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((p, i) => (
                      <tr key={p.id}
                        className="transition-colors"
                        style={{ borderBottom: i < filtered.length - 1 ? `1px solid ${T.bgSoft}` : "" }}
                        onMouseEnter={e => e.currentTarget.style.background = T.bgSoft}
                        onMouseLeave={e => e.currentTarget.style.background = ""}>
                        <td className="px-4 py-4">
                          <input type="checkbox" checked={selected.includes(p.id)}
                            onChange={e => setSelected(s => e.target.checked ? [...s, p.id] : s.filter(x => x !== p.id))}
                            style={{ accentColor: T.gold }} />
                        </td>
                        <td className="px-4 py-4">
  <div className="flex flex-col">
    <p className="text-[13px] font-semibold" style={{ color: T.textPri }}>
      ₹{p.salePrice || p.price || "—"}
    </p>
    {p.salePrice && p.price && Number(p.salePrice) < Number(p.price) && (
      <p className="text-[10px] line-through" style={{ color: T.textSec }}>
        ₹{p.price}
      </p>
    )}
  </div>
</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                              style={{ background: T.goldL, border: `1px solid ${T.champagne}` }}>
                              {p.images?.[0]?.url
                                ? <img src={p.images[0].url} alt={p.title} className="w-full h-full object-cover" />
                                : <span className="text-xl">📓</span>}
                            </div>
                            <div>
                              <p className="text-[13px] font-semibold" style={{ color: T.textPri }}>{p.title}</p>
                              <p className="text-[10px] mt-0.5" style={{ color: T.textSec }}>
                                {p.subtitle && <span>{p.subtitle} · </span>}
                                <span className="font-mono">{p.sku || "No SKU"}</span>
                              </p>
                              {p.slug && (
                                <p className="text-[10px]" style={{ color: T.gold }}>/{p.slug}</p>
                              )}
                            </div>
                          </div>
                        </td>
                      <td className="px-4 py-4">
                      <span className="text-[12px]" style={{ color: T.textSec }}>
                        {p.categoryName || "—"}
                      </span>
                    </td>
                        <td className="px-4 py-4">
                          <p className="text-[13px] font-semibold"
                            style={{ color: Number(p.stock) === 0 ? T.danger : Number(p.stock) < 10 ? T.warning : T.textPri }}>
                            {p.stock ?? "—"}
                          </p>
                          {Number(p.stock) < 10 && Number(p.stock) > 0 && (
                            <p className="text-[9px]" style={{ color: T.warning }}>Low stock</p>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col gap-1">
                            <StatusBadge status={p.status} />
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-wrap gap-1">
                            {p.isFeatured   && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: T.goldL, color: T.goldD }}>⭐ Featured</span>}
                            {p.isBestSeller && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700">🏆 Best Seller</span>}
                            {p.isNewArrival && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">✨ New</span>}
                            {p.isTrending   && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700">🔥 Trending</span>}
                            {p.isPersonalizable && <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: T.bgSoft, color: T.textSec }}>✏️ Custom</span>}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-1.5">
                            <button onClick={() => { setEditing(p); setShowForm(true); }}
                              className="px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors hover:bg-amber-50"
                              style={{ borderColor: T.border, color: T.textSec }}>
                              Edit
                            </button>
                            <button onClick={() => setDeleteConfirm(p.id)}
                              className="px-3 py-1.5 rounded-lg text-[11px] font-medium border transition-colors hover:bg-red-50"
                              style={{ borderColor: T.border, color: T.danger }}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Category-wise count */}
          <Card>
            
            <SectionTitle>Products by Category</SectionTitle>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 sm:gap-3">
              {CATEGORIES.map(cat => {
              const count = products.filter(p =>
  (p.categoryName || "").trim().toLowerCase() === cat.label.trim().toLowerCase()
).length;
                return (
                  <div key={cat.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors cursor-pointer"
                    style={{ background: T.bgSoft }}
                    onClick={() => { setFilterCat(cat.id); setTab("all"); }}>
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold truncate" style={{ color: T.textPri }}>{cat.label}</p>
                      <p className="text-[10px]" style={{ color: T.textSec }}>{cat.group}</p>
                    </div>
                    <span className="text-[13px] font-bold flex-shrink-0 ml-2" style={{ color: count > 0 ? T.gold : T.textSec }}>{count}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}

      {/* Product Form Modal */}
      {showForm && (
        <ProductForm
          product={editing || {}}
          saving={saving}
          onSave={handleSave}
          onCancel={() => { if (!saving) { setShowForm(false); setEditing(null); } }}
        />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="rounded-3xl p-6 sm:p-8 w-full max-w-xs text-center" style={{ background: T.card }}>
            <div className="text-4xl mb-3">🗑️</div>
            <h3 className="text-[16px] font-bold mb-2" style={{ color: T.textPri }}>Delete Product?</h3>
            <p className="text-[12px] mb-6" style={{ color: T.textSec }}>
              This will permanently delete <strong>{products.find(p => p.id === deleteConfirm)?.title}</strong>. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-medium border"
                style={{ borderColor: T.border, color: T.textSec }}>Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold text-white"
                style={{ background: T.danger }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}